import React, { useEffect, useState } from 'react';
import type { PickerItem } from './ManualPicker';

interface EditMappingModalProps {
    open: boolean;
    onClose: () => void;
    srcLabel: string;
    tgtLabel: string;
    lockedSrc: { name: string; sub?: string };
    tgtItems: PickerItem[];
    loading?: boolean;
    onMap: (tgt: PickerItem) => Promise<void>;
    onError?: (message: string) => void;
}

export const EditMappingModal: React.FC<EditMappingModalProps> = ({
    open, onClose, srcLabel, tgtLabel, lockedSrc, tgtItems, loading = false, onMap, onError,
}) => {
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState<PickerItem | null>(null);
    const [mapping, setMapping] = useState(false);

    useEffect(() => {
        if (!open) { setQuery(''); setSelected(null); }
    }, [open]);

    if (!open) return null;

    const filtered = tgtItems.filter(i =>
        !query || i.name.toLowerCase().includes(query.toLowerCase()) || (i.sub ?? '').toLowerCase().includes(query.toLowerCase())
    );

    const handleMap = async () => {
        if (!selected) return;
        setMapping(true);
        try {
            await onMap(selected);
            onClose();
        } catch (err) {
            onError?.(err instanceof Error ? err.message : 'Failed to update mapping');
        } finally {
            setMapping(false);
        }
    };

    return (
        <div
            onClick={onClose}
            className="mapper-ui font-sans fixed inset-0 flex items-center justify-center z-[100] p-5"
            style={{ background: 'rgba(24,33,48,.42)' }}
        >
            <div
                onClick={e => e.stopPropagation()}
                className="bg-mapper-white rounded-2xl max-w-[480px] w-full overflow-hidden shadow-mapper-modal"
            >
                <div className="px-6 py-5 border-b border-mapper-border-3 flex items-center justify-between">
                    <h3 className="text-base font-bold m-0">Edit mapping</h3>
                    <button onClick={onClose} className="w-[30px] h-[30px] rounded-lg text-mapper-muted-3 text-xl flex items-center justify-center">×</button>
                </div>

                <div className="px-6 pt-5">
                    <div className="text-[11px] font-bold text-mapper-muted-3 tracking-[0.4px] mb-2">{srcLabel}</div>
                    <div className="border border-mapper-purple-border-3 bg-mapper-purple-bg-3 rounded-[10px] px-[13px] py-[11px] mb-5">
                        <div className="text-[13.5px] font-semibold text-mapper-text">{lockedSrc.name}</div>
                        {lockedSrc.sub && <div className="text-[11.5px] text-mapper-muted-2">{lockedSrc.sub}</div>}
                    </div>

                    <div className="text-[11px] font-bold text-mapper-muted-3 tracking-[0.4px] mb-2">{tgtLabel}</div>
                    <input
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Search…"
                        className="w-full px-3 py-2 border border-mapper-border-2 rounded-[9px] text-[13px] outline-none bg-mapper-surface-2 mb-2"
                    />
                    <div className="max-h-[260px] overflow-y-auto -mx-1 px-1">
                        {loading ? (
                            <div className="text-[13px] text-mapper-muted-2 px-2 py-4">Loading…</div>
                        ) : filtered.length === 0 ? (
                            <div className="text-[13px] text-mapper-muted-2 px-2 py-4">No items found</div>
                        ) : (
                            filtered.map(item => (
                                <button
                                    key={item.id}
                                    onClick={() => setSelected(item)}
                                    className={`block w-full text-left px-[13px] py-[10px] rounded-[9px] mb-[3px] border ${
                                        selected?.id === item.id ? 'bg-mapper-teal-bg-3 border-mapper-teal-border-3' : 'border-transparent'
                                    }`}
                                >
                                    <div className="text-[13.5px] font-semibold text-mapper-text">{item.name}</div>
                                    {item.sub && <div className="text-[11.5px] text-mapper-muted-2">{item.sub}</div>}
                                </button>
                            ))
                        )}
                    </div>
                </div>

                <div className="px-6 py-4 mt-2 border-t border-mapper-border-3 flex items-center justify-end gap-3">
                    <button onClick={onClose} disabled={mapping} className="px-4 py-2 rounded-[9px] text-[13px] font-semibold text-mapper-muted">
                        Cancel
                    </button>
                    <button
                        onClick={handleMap}
                        disabled={!selected || mapping}
                        className={`px-5 py-2 rounded-[9px] text-[13px] font-bold ${
                            selected && !mapping ? 'bg-mapper-teal text-white shadow-mapper-btn' : 'bg-mapper-gray-bg text-mapper-muted-5 cursor-default'
                        }`}
                    >
                        {mapping ? 'Mapping…' : 'Map'}
                    </button>
                </div>
            </div>
        </div>
    );
};
